import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Calendar, Bell, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TASK_TYPES, JOB_STAGES, JOB_STATUSES } from '../utils/constants';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './EventModal.css';

const PRESET_TYPES = TASK_TYPES.filter(t => t !== 'Other');


const EMPTY_FORM = {
  title: '',
  type: 'HR Interview',
  job: '',
  date: '',
  endDate: '',
  isVirtual: true,
  venue: '',
  meetingLink: '',
  contacts: [{ name: '', role: '', email: '', phone: '' }],
  notes: '',
  remindByEmail: false,
  reminderDayBefore: true,
  reminderDayOf: true,
};

const SUGGEST_STAGE = {
  'HR Screening Call':           { stage: 'HR Screening',      status: 'Screening' },
  'HR Interview':                { stage: 'HR Interview',       status: 'Interview' },
  'Technical Interview':         { stage: 'Technical Interview',status: 'Interview' },
  'Technical Exam / Assessment': { stage: 'Technical Exam',     status: 'Assessment' },
  'Final Interview':             { stage: 'Final Interview',    status: 'Interview' },
  'Offer Discussion':            { stage: 'Offer Negotiation',  status: 'Offer' },
};

const EventModal = ({ event, jobs, defaultDate, customTypes: propCustomTypes = [], onCustomTypesChange, onClose, onAdd, onUpdate, onDelete, onUpgrade }) => {
  const { user } = useAuth();
  const isPro = user?.tier === 'pro';
  const isEditing = !!event;
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [askJobUpdate, setAskJobUpdate] = useState(false);
  const [jobUpdateChoice, setJobUpdateChoice] = useState({ stage: '', status: '' });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const customTypes = propCustomTypes;
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(true);
  const [yourTypesOpen, setYourTypesOpen] = useState(true);
  const [customTypeName, setCustomTypeName] = useState('');
  const [customTypeColor, setCustomTypeColor] = useState('#6366F1');
  const [saveAsCustomType, setSaveAsCustomType] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [typeDeleteEvents, setTypeDeleteEvents] = useState(null);
  const [loadingDeleteEvents, setLoadingDeleteEvents] = useState(false);
  const typeDropdownRef = useRef(null);

  useEffect(() => {
    if (event) {
      setForm({
        ...EMPTY_FORM,
        ...event,
        job: event.job?._id || event.job || '',
        date: event.date ? format(new Date(event.date), "yyyy-MM-dd'T'HH:mm") : '',
        endDate: event.endDate ? format(new Date(event.endDate), "yyyy-MM-dd'T'HH:mm") : '',
        contacts: event.contacts?.length ? event.contacts : [{ name: '', role: '', email: '', phone: '' }],
        remindByEmail: event.remindByEmail || false,
        reminderDayBefore: event.reminderDayBefore !== false,
        reminderDayOf: event.reminderDayOf !== false,
      });
    } else if (defaultDate) {
      const d = format(defaultDate, 'yyyy-MM-dd');
      setForm(f => ({ ...f, date: `${d}T09:00` }));
    }
  }, [event, defaultDate]);

  useEffect(() => {
    const handler = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target))
        setTypeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // When type changes, pre-suggest job stage update
  useEffect(() => {
    if (!isEditing && form.job && SUGGEST_STAGE[form.type]) {
      const s = SUGGEST_STAGE[form.type];
      setJobUpdateChoice({ stage: s.stage, status: s.status });
      setAskJobUpdate(true);
    } else if (!form.job) {
      setAskJobUpdate(false);
    }
  }, [form.type, form.job, isEditing]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const setContact = (i, key, val) => {
    const updated = form.contacts.map((c, idx) => idx === i ? { ...c, [key]: val } : c);
    set('contacts', updated);
  };

  const addContact = () => set('contacts', [...form.contacts, { name: '', role: '', email: '', phone: '' }]);
  const removeContact = (i) => set('contacts', form.contacts.filter((_, idx) => idx !== i));

  const handleTypeDeleteClick = async (e, ct) => {
    e.stopPropagation();
    setTypeToDelete(ct);
    setTypeDeleteEvents(null);
    setLoadingDeleteEvents(true);
    try {
      const res = await api.get(`/api/events?type=${encodeURIComponent(ct.label)}`);
      const now = new Date();
      const past = res.data.events.filter(ev => new Date(ev.date) < now);
      const upcoming = res.data.events.filter(ev => new Date(ev.date) >= now);
      setTypeDeleteEvents({ past, upcoming });
    } catch {
      setTypeDeleteEvents({ past: [], upcoming: [] });
    } finally {
      setLoadingDeleteEvents(false);
    }
  };

  const confirmDeleteCustomType = async () => {
    const updated = customTypes.filter(c => c.label !== typeToDelete.label);
    try {
      await api.put('/api/profile/event-types', { customEventTypes: updated });
      onCustomTypesChange?.(updated);
    } catch {
      toast.error('Failed to remove event type');
    }
    if (form.type === typeToDelete.label) set('type', 'Other');
    setTypeToDelete(null);
    setTypeDeleteEvents(null);
  };

  const selectedJob = jobs.find(j => j._id === form.job);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = true;
    if (!form.date) newErrors.date = true;
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});
    setSaving(true);

    let resolvedType = form.type;
    if (form.type === 'Other' && customTypeName.trim()) {
      resolvedType = customTypeName.trim();
      if (saveAsCustomType) {
        if (!customTypes.find(t => t.label === resolvedType)) {
          const newTypes = [...customTypes, { label: resolvedType, color: customTypeColor }];
          try {
            await api.put('/api/profile/event-types', { customEventTypes: newTypes });
            onCustomTypesChange?.(newTypes);
          } catch {
            toast.error('Failed to save event type');
          }
        }
      }
    }

    const cleanContacts = form.contacts.filter(c => c.name || c.email || c.phone);
    const payload = {
      ...form,
      type: resolvedType,
      contacts: cleanContacts,
      job: form.job || null,
      date: form.date ? new Date(form.date).toISOString() : null,
      endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
      updateJobStage: askJobUpdate,
      newStage: jobUpdateChoice.stage,
      newStatus: jobUpdateChoice.status,
      remindByEmail: form.remindByEmail,
      reminderDayBefore: form.reminderDayBefore,
      reminderDayOf: form.reminderDayOf,
    };

    try {
      if (isEditing) {
        await onUpdate(event._id, payload);
      } else {
        await onAdd(payload);
      }
    } catch (e) {
      // handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="emodal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="emodal-panel">
        {/* Header */}
        <div className="emodal-header">
          <div className="emodal-header-left">
            <div className="emodal-header-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
              </svg>
            </div>
            <div>
              <h2 className="emodal-title">{isEditing ? 'Edit Event' : 'New Event'}</h2>
              <p className="emodal-subtitle">Schedule an interview, exam, or follow-up</p>
            </div>
          </div>
          <button className="emodal-close" onClick={onClose}>✕</button>
        </div>

        <div className="emodal-body">
          {/* Title + Type */}
          <div className="emodal-row-2">
            <div className="ef required">
              <label>Event Title *</label>
              <input className={`einput${errors.title ? ' einput-error' : ''}`} value={form.title}
                onChange={e => { set('title', e.target.value); if (errors.title) setErrors(p => ({ ...p, title: false })); }}
                placeholder="e.g. HR Interview Round 1" />
              {errors.title && <span className="ef-error-msg">Event title is required</span>}
            </div>
            <div className="ef">
              <label>Event Type</label>
              <div className="etype-dropdown-wrap" ref={typeDropdownRef}>
                <button type="button" className="etype-trigger" onClick={() => setTypeDropdownOpen(v => !v)}>
                  <span className="etype-trigger-inner">
                    {(() => {
                      const ct = customTypes.find(c => c.label === form.type);
                      return ct ? <><span className="etype-color-dot" style={{ background: ct.color }} />{form.type}</> : form.type;
                    })()}
                  </span>
                  <ChevronDown size={13} className={`etype-chevron${typeDropdownOpen ? ' open' : ''}`} />
                </button>
                {typeDropdownOpen && (
                  <div className="etype-menu">
                    <div className="etype-group-header" onClick={() => setPresetOpen(v => !v)}>
                      <span>ApplAI Preset</span>
                      {presetOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                    </div>
                    {presetOpen && PRESET_TYPES.map(t => (
                      <div key={t} className={`etype-option${form.type === t ? ' etype-option-selected' : ''}`}
                        onClick={() => { set('type', t); setTypeDropdownOpen(false); }}>
                        {t}
                      </div>
                    ))}
                    {customTypes.length > 0 && (
                      <>
                        <div className="etype-group-header" onClick={() => setYourTypesOpen(v => !v)}>
                          <span>Your Event Types</span>
                          {yourTypesOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                        </div>
                        {yourTypesOpen && customTypes.map(ct => (
                          <div key={ct.label} className={`etype-option etype-option-custom${form.type === ct.label ? ' etype-option-selected' : ''}`}
                            onClick={() => { set('type', ct.label); setTypeDropdownOpen(false); }}>
                            <span className="etype-color-dot" style={{ background: ct.color }} />
                            <span style={{ flex: 1 }}>{ct.label}</span>
                            <button
                              type="button"
                              className="etype-option-remove"
                              onClick={(e) => handleTypeDeleteClick(e, ct)}
                              title={`Remove "${ct.label}"`}
                            >✕</button>
                          </div>
                        ))}
                      </>
                    )}
                    <div className="etype-divider" />
                    <div className={`etype-option${form.type === 'Other' ? ' etype-option-selected' : ''}`}
                      onClick={() => { set('type', 'Other'); setTypeDropdownOpen(false); }}>
                      Other
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Custom event type fields — full-width below the 2-col row */}
          {form.type === 'Other' && (
            <div className={`etype-custom-wrap${!isPro ? ' etype-custom-locked' : ''}`}>
              {!isPro && (
                <div className="etype-lock-overlay" onClick={onUpgrade}>
                  <div className="etype-lock-inner">
                    <div className="etype-lock-header">
                      <Lock size={13} className="etype-lock-icon" />
                      <span className="etype-lock-badge">Pro Feature</span>
                    </div>
                    <div className="etype-lock-title">Custom Event Types</div>
                    <div className="etype-lock-desc">Create and save your own event types with custom colors to better organize your calendar.</div>
                    <button type="button" className="etype-lock-btn">Upgrade to unlock →</button>
                  </div>
                </div>
              )}
              <div className={!isPro ? 'etype-lock-content' : ''}>
                <div className="ef">
                  <label>Custom Event Type<span className="ef-optional">(optional)</span></label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      className="einput"
                      style={{ flex: 1 }}
                      value={customTypeName}
                      onChange={e => setCustomTypeName(e.target.value)}
                      placeholder="e.g. Networking Event, Coffee Chat..."
                      disabled={!isPro}
                    />
                    <div className="etype-color-pick-row">
                      <input
                        type="color"
                        className="etype-color-input"
                        value={customTypeColor}
                        onChange={e => setCustomTypeColor(e.target.value)}
                        disabled={!isPro}
                      />
                      <span className="etype-color-swatch" style={{ background: customTypeColor }} />
                    </div>
                  </div>
                </div>
                <label
                  className={`etoggle-label etype-save-label${!customTypeName.trim() || !isPro ? ' etype-save-label-disabled' : ''}`}
                  style={{ marginTop: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={saveAsCustomType}
                    onChange={e => setSaveAsCustomType(e.target.checked)}
                    disabled={!customTypeName.trim() || !isPro}
                  />
                  Save "{customTypeName.trim() || '...'}" to Your Event Types
                </label>
              </div>
            </div>
          )}

          {/* Linked Job */}
          <div className="ef">
            <label>Linked Job Application</label>
            <select className="eselect" value={form.job} onChange={e => set('job', e.target.value)}>
              <option value="">— Not linked to a job —</option>
              {jobs.map(j => (
                <option key={j._id} value={j._id}>{j.company} — {j.role}</option>
              ))}
            </select>
            {selectedJob && (
              <div className="elinked-job-info">
                <span className="elinked-job-status" style={{ opacity: 0.8 }}>
                  Current: {selectedJob.status}{selectedJob.stage ? ` · ${selectedJob.stage}` : ''}
                </span>
              </div>
            )}
          </div>

          {/* Ask to update job */}
          {!isEditing && askJobUpdate && selectedJob && SUGGEST_STAGE[form.type] && (
            <div className="ejob-update-prompt">
              <div className="ejob-update-icon">💡</div>
              <div className="ejob-update-body">
                <p className="ejob-update-q">
                  Update <strong>{selectedJob.company}</strong>'s status to match this event?
                </p>
                <div className="ejob-update-opts">
                  <div className="ef-mini">
                    <label>Stage</label>
                    <select className="eselect-mini" value={jobUpdateChoice.stage}
                      onChange={e => setJobUpdateChoice(j => ({ ...j, stage: e.target.value }))}>
                      <option value="">— keep current —</option>
                      {JOB_STAGES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="ef-mini">
                    <label>Status</label>
                    <select className="eselect-mini" value={jobUpdateChoice.status}
                      onChange={e => setJobUpdateChoice(j => ({ ...j, status: e.target.value }))}>
                      <option value="">— keep current —</option>
                      {JOB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ejob-update-toggle">
                  <label className="etoggle-label">
                    <input type="checkbox" checked={askJobUpdate}
                      onChange={e => setAskJobUpdate(e.target.checked)} />
                    Yes, update the job application
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Date & Time */}
          <div className="ef-section-label">Date & Time</div>
          <div className="emodal-row-2">
            <div className="ef required">
              <label>Start Date & Time *</label>
              <input className={`einput${errors.date ? ' einput-error' : ''}`} type="datetime-local" value={form.date}
                onChange={e => { set('date', e.target.value); if (errors.date) setErrors(p => ({ ...p, date: false })); }} />
              {errors.date && <span className="ef-error-msg">Start date and time are required</span>}
            </div>
            <div className="ef">
              <label>End Date & Time <span className="ef-optional">(optional)</span></label>
              <input className="einput" type="datetime-local" value={form.endDate}
                onChange={e => set('endDate', e.target.value)} />
            </div>
          </div>

          {/* Location */}
          <div className="ef-section-label">Location</div>
          <div className="elocation-toggle">
            <button
              className={`eloc-btn ${form.isVirtual ? 'active' : ''}`}
              onClick={() => set('isVirtual', true)}
            >
              🖥️ Virtual / Online
            </button>
            <button
              className={`eloc-btn ${!form.isVirtual ? 'active' : ''}`}
              onClick={() => set('isVirtual', false)}
            >
              📍 Face-to-Face
            </button>
          </div>

          {form.isVirtual ? (
            <div className="ef">
              <label>Meeting Link</label>
              <input className="einput" type="url" value={form.meetingLink}
                onChange={e => set('meetingLink', e.target.value)}
                placeholder="https://meet.google.com/... or Zoom/Teams link" />
            </div>
          ) : (
            <div className="ef">
              <label>Venue / Address</label>
              <input className="einput" value={form.venue}
                onChange={e => set('venue', e.target.value)}
                placeholder="e.g. 30F Tower 1, BGC, Taguig" />
            </div>
          )}

          {/* Contacts */}
          <div className="ef-section-label">
            Contacts / Interviewers
            <button className="econtact-add-btn" onClick={addContact}>+ Add</button>
          </div>

          {form.contacts.map((contact, i) => (
            <div key={i} className="econtact-row">
              <div className="econtact-grid">
                <div className="ef">
                  <label>Name</label>
                  <input className="einput" value={contact.name}
                    onChange={e => setContact(i, 'name', e.target.value)}
                    placeholder="Jane Reyes" />
                </div>
                <div className="ef">
                  <label>Role / Title</label>
                  <input className="einput" value={contact.role}
                    onChange={e => setContact(i, 'role', e.target.value)}
                    placeholder="HR Manager" />
                </div>
                <div className="ef">
                  <label>Email</label>
                  <input className="einput" type="email" value={contact.email}
                    onChange={e => setContact(i, 'email', e.target.value)}
                    placeholder="jane@company.com" />
                </div>
                <div className="ef">
                  <label>Phone / Mobile</label>
                  <input className="einput" type="tel" value={contact.phone}
                    onChange={e => setContact(i, 'phone', e.target.value)}
                    placeholder="+63 917 ..." />
                </div>
              </div>
              {form.contacts.length > 1 && (
                <button className="econtact-remove" onClick={() => removeContact(i)}>✕</button>
              )}
            </div>
          ))}

          {/* Email Reminder */}
          <div className={`ereminder-section ${!isPro ? 'ereminder-locked' : ''}`}>
            <div className="ereminder-header">
              <div className="ereminder-header-left">
                <div className="ereminder-bell-icon">
                  {isPro
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
                    : <Lock size={14} />
                  }
                </div>
                <div>
                  <div className="ereminder-title">
                    Email Reminders
                    {!isPro && <span className="ereminder-pro-badge">Pro</span>}
                  </div>
                  <div className="ereminder-sub">{isPro ? 'Get emailed before this event' : 'Upgrade to Pro to enable email reminders'}</div>
                </div>
              </div>
              {isPro ? (
                <label className="etoggle-switch">
                  <input
                    type="checkbox"
                    checked={form.remindByEmail}
                    onChange={e => set('remindByEmail', e.target.checked)}
                  />
                  <span className="etoggle-track">
                    <span className="etoggle-thumb" />
                  </span>
                </label>
              ) : (
                <button className="ereminder-upgrade-btn" onClick={onUpgrade}>Upgrade →</button>
              )}
            </div>

            {isPro && form.remindByEmail && (
              <div className="ereminder-options">
                <label className="ereminder-opt">
                  <input
                    type="checkbox"
                    checked={form.reminderDayBefore}
                    onChange={e => set('reminderDayBefore', e.target.checked)}
                  />
                  <span className="ereminder-opt-icon"><Calendar size={14} /></span>
                  <div>
                    <div className="ereminder-opt-label">Day before</div>
                    <div className="ereminder-opt-sub">Reminder email ~24 hrs before</div>
                  </div>
                </label>
                <label className="ereminder-opt">
                  <input
                    type="checkbox"
                    checked={form.reminderDayOf}
                    onChange={e => set('reminderDayOf', e.target.checked)}
                  />
                  <span className="ereminder-opt-icon"><Bell size={14} /></span>
                  <div>
                    <div className="ereminder-opt-label">Day of event</div>
                    <div className="ereminder-opt-sub">Reminder email on the day</div>
                  </div>
                </label>
                <div className="ereminder-smtp-note">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Requires SMTP email configured in your server <code>.env</code> file (SMTP_USER, SMTP_PASS)
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="ef">
            <label>Notes / Preparation</label>
            <textarea className="etextarea" value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              placeholder="Topics to prepare, things to bring, dress code, etc." />
          </div>
        </div>

        {/* Footer */}
        <div className="emodal-footer">
          {isEditing && (
            <button className="efoot-delete" onClick={() => setShowDeleteConfirm(true)}>
              🗑️ Delete
            </button>
          )}
          <div className="efoot-right">
            <button className="efoot-cancel" onClick={onClose}>Cancel</button>
            <button className="efoot-save" onClick={handleSubmit} disabled={saving}>
              {saving
                ? <><span className="btn-spinner" /> Saving...</>
                : isEditing ? '✓ Save Changes' : '+ Add Event'
              }
            </button>
          </div>
        </div>

        {/* Custom type delete confirm */}
        {typeToDelete && (
          <div className="emodal-confirm-overlay">
            <div className="emodal-confirm" style={{ maxWidth: 380 }}>
              <h3>Remove Event Type?</h3>
              <p>Remove <strong>"{typeToDelete.label}"</strong> from your event types?</p>
              {loadingDeleteEvents && (
                <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Checking existing events...</p>
              )}
              {typeDeleteEvents && (typeDeleteEvents.past.length > 0 || typeDeleteEvents.upcoming.length > 0) && (
                <div className="etype-delete-events">
                  {typeDeleteEvents.upcoming.length > 0 && (
                    <div className="etype-delete-group">
                      <div className="etype-delete-group-label">Upcoming Events</div>
                      {typeDeleteEvents.upcoming.map(ev => (
                        <div key={ev._id} className="etype-delete-event-item">
                          {ev.title}
                          <span className="etype-delete-event-date">
                            {format(new Date(ev.date), 'MMM d, yyyy')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {typeDeleteEvents.past.length > 0 && (
                    <div className="etype-delete-group">
                      <div className="etype-delete-group-label">Past Events</div>
                      {typeDeleteEvents.past.map(ev => (
                        <div key={ev._id} className="etype-delete-event-item">
                          {ev.title}
                          <span className="etype-delete-event-date">
                            {format(new Date(ev.date), 'MMM d, yyyy')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                    These events will keep their type label but the color will revert to default.
                  </p>
                </div>
              )}
              {typeDeleteEvents && typeDeleteEvents.past.length === 0 && typeDeleteEvents.upcoming.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No events use this type.</p>
              )}
              <div className="emodal-confirm-actions">
                <button className="efoot-cancel" onClick={() => { setTypeToDelete(null); setTypeDeleteEvents(null); }}>Cancel</button>
                <button className="efoot-delete-confirm" onClick={confirmDeleteCustomType} disabled={loadingDeleteEvents}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {showDeleteConfirm && (
          <div className="emodal-confirm-overlay">
            <div className="emodal-confirm">
              <h3>Delete Event?</h3>
              <p>Remove <strong>"{event?.title}"</strong>? This can't be undone.</p>
              <div className="emodal-confirm-actions">
                <button className="efoot-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button className="efoot-delete-confirm" onClick={() => onDelete(event._id)}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventModal;
